/**
 * UI Library Route
 *
 * Internal UI Library showcase (Admin + Developer only).
 */

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { UILibraryPage } from '@pages/ui-library';
import { useAbility } from '@features/authorization';
import { AuthLoading, useAuth } from '@features/auth';

import type { AppAbility } from '@shared/types/ability';
import type { RouterContext } from '@shared/config/router-context';

const canAccessUiLibrary = (ability: AppAbility) =>
  ability.can('manage', 'all') || ability.can('read', 'UiLibrary');

export const Route = createFileRoute('/$locale/ui-library/')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;

    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/login', params: { locale: params.locale } });
    }

    if (!canAccessUiLibrary(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: UILibraryRoute,
});

export function UILibraryRoute() {
  const { locale } = Route.useParams();
  const navigate = useNavigate();
  const ability = useAbility();
  const auth = useAuth();
  const hasAccess = canAccessUiLibrary(ability);

  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      void navigate({ to: '/$locale/login', params: { locale }, replace: true });
      return;
    }

    if (!hasAccess) {
      void navigate({ to: '/$locale/unauthorized', params: { locale }, replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, hasAccess, locale, navigate]);

  if (auth.isLoading) {
    return <AuthLoading />;
  }

  if (!auth.isAuthenticated) {
    return <AuthLoading message="Redirecting to login..." />;
  }

  if (!hasAccess) {
    return <AuthLoading message="Redirecting to unauthorized..." />;
  }

  return <UILibraryPage locale={locale} />;
}
